import { ArrowRightOutlined, CloudSyncOutlined, IdcardOutlined, LaptopOutlined, NotificationOutlined, TeamOutlined, WalletOutlined } from '@ant-design/icons';
import { Button, Empty, Tag } from 'antd';
import { api } from '../api';
import { Bone, ErrorNotice, ListSkeleton, PageHeader, Panel, Person, PlanTag, StatCard, SubscriptionTag } from '../components';
import { actionLabel, daysBetween, formatBytes, formatDate, formatRupees, mobileLabel, subscriptionState, timeAgo, todayIso, useLoad } from '../lib';
import { PaymentStatusTag } from './PaymentsPage';
const FEED_TONES = {
    account: 'tone-indigo',
    licence: 'tone-green',
    device: 'tone-amber',
    notification: 'tone-sky',
    storage: 'tone-slate',
    plan: 'tone-indigo',
    payment: 'tone-green',
    sync: 'tone-sky'
};
export function OverviewPage({ go }) {
    const { data, error, loading } = useLoad(async () => {
        const [summary, accounts, subscriptions, audit, storage, files, payments] = await Promise.all([
            api('/v1/admin/summary'),
            api('/v1/admin/accounts'),
            api('/v1/admin/subscriptions'),
            api('/v1/admin/audit'),
            api('/v1/admin/storage'),
            api('/v1/admin/sync-files'),
            api('/v1/admin/payments')
        ]);
        return {
            summary,
            accounts: accounts.accounts,
            subscriptions: subscriptions.subscriptions,
            events: audit.events,
            storage: storage.settings,
            files: files.files,
            payments: payments.payments
        };
    });
    const today = todayIso();
    const summary = data?.summary;
    const renewals = (data?.subscriptions ?? [])
        .filter((row) => ['expiring', 'grace'].includes(subscriptionState(row, today)))
        .sort((a, b) => a.validUntil.localeCompare(b.validUntil))
        .slice(0, 5);
    const synced = data?.files ?? [];
    const storedBytes = synced.reduce((sum, file) => sum + file.sizeBytes, 0);
    // Paid this calendar month, by the UTC day it was paid (as the API keeps dates).
    const month = new Date().toISOString().slice(0, 7);
    const paidThisMonth = (data?.payments ?? []).filter((row) => row.status === 'paid' && (row.paidAt ?? '').slice(0, 7) === month);
    const revenue = paidThisMonth.reduce((sum, row) => sum + row.amountPaise, 0);
    const recentPayments = (data?.payments ?? []).slice(0, 5);
    const mobiles = new Map((data?.accounts ?? []).map((account) => [account.id, mobileLabel(account.mobile)]));
    const accountNames = new Map((data?.accounts ?? []).map((account) => [account.id, account.name]));
    return (<>
      <PageHeader title="Overview" description="How Kivonex Cloud is doing today: customers, payments, plans, computers and synced companies." actions={<Button type="primary" icon={<TeamOutlined />} onClick={() => go('accounts')}>
            Manage accounts
          </Button>}/>
      <ErrorNotice error={error}/>

      <div className="stats">
        <StatCard label="Accounts" tone="indigo" icon={<TeamOutlined />} loading={loading && !data} value={summary?.accounts ?? 0} foot={summary ? `${summary.activeAccounts} active · ${summary.accounts - summary.activeAccounts} disabled` : ' '}/>
        <StatCard label="Subscriptions in date" tone="green" icon={<IdcardOutlined />} loading={loading && !data} value={summary?.activeSubscriptions ?? 0} foot={renewals.length ? `${renewals.length} need renewal soon` : 'None due for renewal'}/>
        <StatCard label="Paid this month" tone="green" icon={<WalletOutlined />} loading={loading && !data} value={<span className="num">{formatRupees(revenue)}</span>} foot={`${paidThisMonth.length} ${paidThisMonth.length === 1 ? 'payment' : 'payments'} · GST included`}/>
        <StatCard label="Computers" tone="amber" icon={<LaptopOutlined />} loading={loading && !data} value={summary?.devices ?? 0} foot="Signed in to an account"/>
        <StatCard label="Synced companies" tone="sky" icon={<CloudSyncOutlined />} loading={loading && !data} value={synced.length} foot={data ? `${formatBytes(storedBytes)} in cloud storage` : ' '}/>
      </div>

      <div className="grid-2">
        <div className="stack">
          <Panel flush title="Newest accounts" description="The latest customers added to Kivonex Cloud." extra={<Button type="link" onClick={() => go('accounts')} style={{ paddingInline: 0 }}>
                View all <ArrowRightOutlined />
              </Button>}>
            {loading && !data ? (<ListSkeleton rows={5}/>) : data?.accounts.length ? (<ul className="feed">
                {data.accounts.slice(0, 6).map((account) => (<li key={account.id} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Person name={account.name} detail={mobileLabel(account.mobile) || account.email || undefined} size={34}/>
                    <span style={{ textAlign: 'right', flex: 'none' }}>
                      {account.disabledAt ? <Tag variant="filled">Disabled</Tag> : <Tag color="success" variant="filled">Active</Tag>}
                      <span className="cell-sub">Joined {formatDate(account.createdAt)}</span>
                    </span>
                  </li>))}
              </ul>) : (<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No accounts yet" style={{ padding: 24 }}/>)}
          </Panel>

          <Panel flush title="Renewals due" description="Subscriptions ending in the next 30 days or in their grace period." extra={<Button type="link" onClick={() => go('subscriptions')} style={{ paddingInline: 0 }}>
                Subscriptions <ArrowRightOutlined />
              </Button>}>
            {loading && !data ? (<ListSkeleton rows={2}/>) : renewals.length ? (<ul className="feed">
                {renewals.map((row) => {
                const left = daysBetween(today, row.validUntil);
                return (<li key={row.id} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Person name={row.accountName} detail={mobiles.get(row.accountId) || row.accountEmail || undefined} size={34}/>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                        <PlanTag plan={row.plan}/>
                        <SubscriptionTag state={subscriptionState(row, today)}/>
                        <span className="cell-sub" style={{ minWidth: 92, textAlign: 'right' }}>
                          {left >= 0 ? `Ends in ${left} ${left === 1 ? 'day' : 'days'}` : `Ended ${-left} days ago`}
                        </span>
                      </span>
                    </li>);
            })}
              </ul>) : (<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing due for renewal" style={{ padding: 24 }}/>)}
          </Panel>
        </div>

        <div className="stack">
          <Panel flush title="Recent payments" extra={<Button type="link" onClick={() => go('payments')} style={{ paddingInline: 0 }}>
                Payments <ArrowRightOutlined />
              </Button>}>
            {loading && !data ? (<ListSkeleton rows={3}/>) : recentPayments.length ? (<ul className="feed">
                {recentPayments.map((row) => (<li key={row.id} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Person name={row.accountName} detail={`${row.planName} · ${timeAgo(row.paidAt ?? row.createdAt)}`} size={34}/>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                      <span className="num" style={{ fontWeight: 550 }}>{formatRupees(row.amountPaise)}</span>
                      <PaymentStatusTag status={row.status}/>
                    </span>
                  </li>))}
              </ul>) : (<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payments yet" style={{ padding: 24 }}/>)}
          </Panel>

          <Panel title="Cloud storage" extra={data?.storage.enabled ? (<Tag color="success" variant="filled"><span className="status-dot"/>Sync on</Tag>) : (<Tag variant="filled">Sync off</Tag>)}>
            {loading && !data ? (<div className="bone-lines">
                <Bone width="60%" height={13}/>
                <Bone width="80%" height={10}/>
                <Bone width="70%" height={10}/>
              </div>) : data?.storage.bucket ? (<div style={{ display: 'grid', gap: 6 }}>
                <span>
                  Backblaze B2 bucket <b>{data.storage.bucket}</b>
                </span>
                <span className="cell-sub">{data.storage.endpoint}</span>
                <span className="cell-sub">
                  {synced.length} {synced.length === 1 ? 'company' : 'companies'} · {formatBytes(storedBytes)} · up to {data.storage.maxUploadMb} MB per company
                </span>
              </div>) : (<div style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
                <span style={{ color: 'var(--muted)' }}>No bucket is set up, so customers cannot sync companies yet.</span>
                <Button onClick={() => go('settings', 'storage')}>Set up cloud storage</Button>
              </div>)}
          </Panel>

          <Panel flush title="Recent activity" extra={<Button type="link" onClick={() => go('audit')} style={{ paddingInline: 0 }}>
                Audit log <ArrowRightOutlined />
              </Button>}>
            {loading && !data ? (<ListSkeleton rows={6} avatar={30} trailing={false}/>) : data?.events.length ? (<ul className="feed">
                {data.events.slice(0, 8).map((event) => (<li key={event.id}>
                    <span className={`feed-icon ${FEED_TONES[event.action.split('.')[0] ?? ''] ?? 'tone-slate'}`}>
                      {event.action.startsWith('notification') ? <NotificationOutlined /> : event.action.startsWith('sync') || event.action.startsWith('storage') ? <CloudSyncOutlined /> : event.action.startsWith('device') ? <LaptopOutlined /> : event.action.startsWith('payment') ? <WalletOutlined /> : event.action.startsWith('licence') || event.action.startsWith('plan') ? <IdcardOutlined /> : <TeamOutlined />}
                    </span>
                    <span className="feed-text">
                      {actionLabel(event.action)}
                      {event.accountId && accountNames.has(event.accountId) ? <> · {accountNames.get(event.accountId)}</> : event.accountEmail && <> · {event.accountEmail}</>}
                      <small>{timeAgo(event.at)}</small>
                    </span>
                  </li>))}
              </ul>) : (<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity yet" style={{ padding: 24 }}/>)}
          </Panel>
        </div>
      </div>
    </>);
}
